import React from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { sq as squareLocale } from "date-fns/locale/sq";

import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { MapPin, Calendar, Star, Truck, Package } from 'lucide-react';
import ProductImage from './ProductImage';
import FavoriteButton from './FavoriteButton';
import { createProductUrl } from '@/utils/slugUtils';

export interface ProductCardProps {
  id: string;
  title: string;
  price: number;
  location: string;
  imageUrl: string;
  category: string;
  subcategory?: string;
  isPremium?: boolean;
  createdAt?: string;
  onToggleFavorite?: (id: string) => void;
  isFavorite?: boolean;
  isLoading?: boolean;
  isNewUpload?: boolean;
  condition?: string;
  deliveryOption?: string;
  user_id?: string;
  is_price_negotiable?: boolean;

  // Vehicle-specific fields
  make?: string;
  model?: string;
  year?: number;
  transmission?: string;
  mileage?: number;
  stock?: number;

  // Added for premium listings and user context
  userProfileImage?: string | null;
  username?: string | null;
}

export const ProductCard = ({
  id,
  title,
  price,
  location,
  imageUrl,
  category,
  subcategory,
  isPremium = false,
  createdAt,
  onToggleFavorite,
  isFavorite,
  isLoading,
  isNewUpload = false,
  condition,
  deliveryOption,
  user_id,
  is_price_negotiable = false,

  // Vehicle-specific fields
  make,
  model,
  year,
  transmission,
  mileage,
  stock,

  // User context props
  userProfileImage,
  username,
}: ProductCardProps) => {
  const formattedDate = createdAt
  ? formatDistanceToNow(new Date(createdAt), { addSuffix: true, locale: squareLocale })
  : '';

  const handleToggleFavorite = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onToggleFavorite) {
      onToggleFavorite(id);
    }
  };

  const isVehicle = category === 'Automjete' || category === 'Automobiles';

  const vehicleTitle = isVehicle && make && model
    ? `${make} ${model}${year ? ` (${year})` : ''}`
    : title;

  const vehicleDetails = isVehicle ? [
    year && `Year: ${year}`,
    transmission && `Transmission: ${transmission}`,
    mileage && `Mileage: ${mileage.toLocaleString()} km`
  ].filter(Boolean).join(' • ') : '';

  const isOutOfStock = stock !== undefined && stock <= 0;
  const isLowStock = stock !== undefined && stock > 1 && stock <= 5;
  
  const hasShipping = deliveryOption && deliveryOption !== 'pickup_only';

  // Create SEO-friendly URL
  const productUrl = createProductUrl(id, title);

  return (
    <Card className="group overflow-hidden transition-all duration-300 relative h-full border rounded-xl hover:shadow-lg hover:scale-[1.02]">
      <Link to={productUrl} className="block h-full">
        <div className="relative">
          <div className="w-full overflow-hidden rounded-t-xl">
            <ProductImage 
              imageUrl={imageUrl} 
              title={title} 
              isLoading={isLoading}
              isNewUpload={isNewUpload}
            />
          </div>

          {isPremium && (
            <div className="absolute top-3 left-3 z-10">
              <Badge className="bg-yellow-500 text-white px-2 py-1 flex items-center gap-1 shadow-sm">
                <Star className="h-3 w-3" />
                <span className="text-xs font-medium">Premium</span>
              </Badge>
            </div>
          )}

          {isOutOfStock && (
            <div className="absolute top-3 right-3 z-10">
              <Badge className="bg-red-500 text-white px-2 py-1 shadow-sm">
                Ska me gjendje
              </Badge>
            </div>
          )}
        </div>

        <CardContent className="p-4">
          <div className="flex flex-col h-full space-y-2">
            <div className="text-xs text-gray-500">
              {category}
              {subcategory && ` - ${subcategory}`}
            </div>
            
            <div className="flex items-start justify-between gap-2">
              <h3 className="font-semibold text-base line-clamp-2 flex-1">
                {isVehicle ? vehicleTitle : title}
              </h3>
              <div className="flex flex-col items-end shrink-0">
                <p className="font-bold text-lg text-brand-600 whitespace-nowrap">
                  {isOutOfStock ? 'Ska me gjendje' : `${price.toLocaleString()} €`}
                </p>
                {is_price_negotiable && (
                  <Badge className="bg-blue-500 hover:bg-blue-600 text-white mt-1 text-xs shadow-sm">
                    I negociueshëm
                  </Badge>
                )}
              </div>
            </div>
            
            {isVehicle && vehicleDetails && (
              <p className="text-xs text-gray-600">{vehicleDetails}</p>
            )}
            
            {isLowStock && (
              <div>
                <Badge variant="outline" className="text-xs flex items-center gap-1 border-amber-300 bg-amber-50 text-amber-700">
                  <Package className="h-3 w-3 mr-1" />
                  <span>Pak në stok: {stock}</span>
                </Badge>
              </div>
            )}

            {hasShipping && (
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline" className="text-xs flex items-center gap-1 border-green-300 bg-green-50 text-green-700">
                  <Truck className="h-3 w-3" />
                  <span>Transport</span>
                </Badge>
                
                {deliveryOption && (
                  <Badge variant="outline" className="text-xs flex items-center gap-1 border-gray-200 bg-gray-50 text-gray-600">
                    {deliveryOption === 'pickup_only' ? (
                      <>
                        <MapPin className="h-3 w-3" />
                        <span>Vetëm Tërheqje</span>
                      </>
                    ) : (
                      <>
                        <Truck className="h-3 w-3" />
                        <span>Transport & Tërheqja</span>
                      </>
                    )}
                  </Badge>
                )}
              </div>
            )}

            <div className="mt-auto pt-2 border-t border-gray-100">
              <div className="flex justify-between items-center">
                <div className="space-y-1">
                  <div className="flex items-center text-gray-500 text-xs">
                    <MapPin className="mr-1 h-3 w-3 text-gray-400" />
                    <span>{location}</span>
                  </div>

                  {createdAt && (
                    <div className="flex items-center text-gray-500 text-xs">
                      <Calendar className="mr-1 h-3 w-3 text-gray-400" />
                      <span>{formattedDate}</span>
                    </div>
                  )}
                </div>

                <div className="flex items-center space-x-1">
                  {onToggleFavorite && (
                    <div onClick={handleToggleFavorite} className="transition-transform hover:scale-110">
                      <FavoriteButton 
                        isFavorite={isFavorite}
                        isLoading={isLoading}
                        onToggle={handleToggleFavorite}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Link>
    </Card>
  );
};

export default ProductCard;
